// Pure helpers for the CMA comp review (kept out of the component so they can be unit tested).

export const MAX_COMPS = 10;
export const MIN_COMPS = 3;
const MS_PER_DAY = 86_400_000;

const toIntOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : null;
};

const toMoneyOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

export const isoDay = (d) => d.toISOString().slice(0, 10);

/** Earliest and latest sale date a manual comp may have (the last 12 months, not in the future). */
export const manualSaleDateBounds = (now = new Date()) => ({
  min: isoDay(new Date(now.getTime() - 365 * MS_PER_DAY)),
  max: isoDay(now),
});

export const emptyManualForm = () => ({
  address: '', city: '', state: '', zip: '', salePrice: '', saleDate: '',
  squareFeet: '', bedrooms: '', bathrooms: '', yearBuilt: '', notes: '',
});

/** Returns { errors: {field: msg}, comp } - comp is the API payload when there are no errors. */
export const validateManualComp = (form, now = new Date()) => {
  const errors = {};
  if (!form.address.trim()) errors.address = 'Address is required';
  const price = toMoneyOrNull(form.salePrice);
  if (price === null || price < 10000 || price > 100000000) errors.salePrice = 'Enter the sale price in dollars';
  const sqft = toIntOrNull(form.squareFeet);
  if (sqft === null || sqft < 300 || sqft > 20000) errors.squareFeet = 'Square feet must be 300 - 20,000';
  const { min, max } = manualSaleDateBounds(now);
  if (!form.saleDate) errors.saleDate = 'Sale date is required';
  else if (form.saleDate > max) errors.saleDate = 'Sale date cannot be in the future';
  else if (form.saleDate < min) errors.saleDate = 'Sale date must be within the last 12 months';
  const beds = toIntOrNull(form.bedrooms);
  if (form.bedrooms !== '' && (beds === null || beds < 0 || beds > 20)) errors.bedrooms = '0 - 20';
  const baths = toIntOrNull(form.bathrooms);
  if (form.bathrooms !== '' && (baths === null || baths < 0 || baths > 20)) errors.bathrooms = '0 - 20';
  const year = toIntOrNull(form.yearBuilt);
  if (form.yearBuilt !== '' && (year === null || year < 1800 || year > 2100)) errors.yearBuilt = 'Enter a 4-digit year';
  if (form.state.trim() && form.state.trim().length !== 2) errors.state = '2 letters';
  if (Object.keys(errors).length) return { errors, comp: null };

  const comp = {
    address: form.address.trim(),
    salePrice: price,
    saleDate: form.saleDate,
    squareFeet: sqft,
  };
  if (form.city.trim()) comp.city = form.city.trim();
  if (form.state.trim()) comp.state = form.state.trim().toUpperCase();
  if (form.zip.trim()) comp.zip = form.zip.trim();
  if (beds !== null) comp.bedrooms = beds;
  if (baths !== null) comp.bathrooms = baths;
  if (year !== null) comp.yearBuilt = year;
  if (form.notes.trim()) comp.notes = form.notes.trim();
  return { errors: {}, comp };
};

/** Subject override fields shown in the form, as strings, from the draft's subject property. */
export const subjectToForm = (subject = {}) => ({
  bedrooms: subject.bedrooms ?? '',
  bathrooms: subject.bathrooms ?? '',
  squareFeet: subject.squareFeet ?? '',
  lotSizeSqFt: subject.lotSizeSqFt ?? '',
  yearBuilt: subject.yearBuilt ?? '',
  propertyType: subject.propertyType ?? '',
});

/**
 * Builds the subjectOverrides payload: only fields the agent changed from the recorded subject, plus
 * the upgrades that have both a description and a positive value. Returns undefined when nothing applies.
 */
export const buildSubjectOverrides = (original = {}, form = {}, upgrades = []) => {
  const out = {};
  [['bedrooms', 0, 20], ['bathrooms', 0, 20], ['squareFeet', 300, 20000], ['lotSizeSqFt', 0, 5000000], ['yearBuilt', 1800, 2100]]
    .forEach(([key, lo, hi]) => {
      const v = toIntOrNull(form[key]);
      if (v !== null && v >= lo && v <= hi && v !== (original[key] ?? null)) out[key] = v;
    });
  const type = (form.propertyType || '').trim();
  if (type && type !== (original.propertyType || '')) out.propertyType = type;

  const ups = upgrades
    .map((u) => ({ description: (u.description || '').trim(), value: toMoneyOrNull(u.value) }))
    .filter((u) => u.description && u.value !== null && u.value >= 1 && u.value <= 5000000);
  if (ups.length) out.upgrades = ups;
  return Object.keys(out).length ? out : undefined;
};

/** The request body for POST /draft/{id}/generate. */
export const buildGenerateRequest = ({ selectedIds, manualComps, subject, subjectForm, upgrades }) => {
  const body = { selectedCompIds: Array.from(selectedIds), manualComps: manualComps.map((m) => m.comp) };
  const overrides = buildSubjectOverrides(subject, subjectForm, upgrades);
  if (overrides) body.subjectOverrides = overrides;
  return body;
};

/** How many comps the report will use and what the agent should know about it. */
export const compCountStatus = (selectedCount, manualCount) => {
  const total = selectedCount + manualCount;
  return {
    total,
    canGenerate: total >= MIN_COMPS,
    overLimit: total > MAX_COMPS,
    message: total < MIN_COMPS
      ? `Select at least ${MIN_COMPS} comparables (${total} chosen).`
      : total > MAX_COMPS
        ? `${total} chosen. Only the ${MAX_COMPS} best matches by similarity score will be used.`
        : `${total} of ${MAX_COMPS} comparables chosen.`,
  };
};

/** Initial selection: the agent's previous choice when reopening, else the automatic top 10. */
export const initialSelection = (draft) => {
  const known = new Set((draft.candidates || []).map((c) => c.compId));
  const prev = draft.lastRequest?.selectedCompIds;
  const ids = (prev && prev.length ? prev : draft.autoSelectedCompIds || []).filter((id) => known.has(id));
  return new Set(ids);
};
